// @flow
import type { Node } from 'react';
import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';

import {
  Provider as StoreProvider,
  useDispatch,
  useSelector
} from 'react-redux';

import { ImageProvider } from '@contexts/image';
import { VideoProvider, VideoContext } from '@contexts/video';
import { SkuContext, SkuProvider } from '@contexts/sku';
import {
  SettingStateContext,
  SettingDispatchContext,
  SettingProvider
} from '@contexts/setting';
import {
  CompatibilityContext,
  CompatibilityProvider
} from '../../../contexts/compatibility';

import useProductDetailColumnCount from '@hooks/useProductDetailColumnCount';

import DyorDataWrapper from '@components/DyorDataWrapper';

import DesignYourOwnRing from '@components/dyor/Container';
import PageFooter from '@components/productDetail/PageFooter.jsx';
import RecentlyViewed from '@components/RecentlyViewedSettings';
import OtherMetals from '@components/productDetail/OtherMetals';
import StoneSizeSlider from '@components/productDetail/engagementRings/StoneSizeSlider';
import Specifications from '@components/productDetail/engagementRings/Specifications';
import ShippingAndActions from '@components/productDetail/engagementRings/ShippingAndActions';
import ImageThumbnails from '@components/productDetail/engagementRings/ImageThumbnails';
import DescriptionAndPrice from '@components/productDetail/engagementRings/DescriptionAndPrice';
import MainImage from '@components/productDetail/engagementRings/MainImage';
import DiamondCompatibility from '@components/productDetail/engagementRings/DiamondCompatibility';
import MatchingBandCallout from '@components/productDetail/MatchingBandCallout';

import GetUpdatedProductData from '../../async_data_retrievers/EngagementRing';

import ErrorBoundary from '@components/ErrorBoundary';

import AxiosInstanceGenerator from '@src/utils/axiosInstance';
import { handleError } from '../../../utils/errors';

import * as routes from '@src/app_routes/settings';

import css from '@styles/productDetail';
import { itemIndexableSeries } from '../../../utils/favorites';

import ViewItemEvent from '@components/productDetail/googleAnalyticsEvents/viewItem/engagementRing';

import type { TSettingDetail } from '@types/setting';
import type { RailsContextType } from '@types/misc';

declare var Honeybadger: any;

type TContainerProps = {
  children: Node
};

type PropsType = {};

const urlMatcher = /\/engagement-rings\/detail(?:\/[^/].+)?\/(.*)$/;
const loadingIcon = '/images/loading-diamond.gif';

function Container({ children }: TContainerProps) {
  return (
    <div
      className={`${css.container} product-detail--container engagement-ring`}
    >
      {' '}
      {children}{' '}
    </div>
  );
}

function LeftAndMiddle() {
  return (
    <div className={css.leftAndMiddle}>
      <Left />
      <Middle />
    </div>
  );
}

function Left() {
  const columnCount = useProductDetailColumnCount();

  if (columnCount >= 3) {
    return (
      <div className={css.left}>
        <Specifications />
      </div>
    );
  } else {
    return null;
  }
}

function ImageSection() {
  const { toggleVideoState } = useContext(VideoContext);
  const { setting }: { setting: TSettingDetail } = useContext(
    SettingStateContext
  );
  const dispatch = useDispatch();
  const isFavorited = useSelector(state => {
    const series = state.settingFavorites.series || [];

    return (
      series.indexOf(
        itemIndexableSeries(setting.lumera_series, setting.raw_metal)
      ) !== -1
    );
  });

  const handleFavoriteToggle = useCallback(() => {
    dispatch({
      type: 'TOGGLE_FAVORITE_SETTING',
      item: {
        sku: setting.vendor_sku,
        series: itemIndexableSeries(setting.lumera_series, setting.raw_metal)
      }
    });
  }, [dispatch, setting.vendor_sku, setting.lumera_series, setting.raw_metal]);

  return (
    <MainImage
      onClick={toggleVideoState}
      isFavorited={isFavorited}
      onToggleFavorite={handleFavoriteToggle}
    />
  );
}

function Middle() {
  const diamondId = useSelector(state => state.ringBuilder.diamond.id);
  const columnCount = useProductDetailColumnCount();

  return (
    <div className={css.middle}>
      {!diamondId && <StoneSizeSlider />}
      <ImageThumbnails />
      {columnCount < 3 ? <Specifications /> : null}
      <OtherMetals />
      <RecentlyViewed />
      {/*
        We use flexbox order: -1 to display this first. It is placed here in
       the markup because otherwise other elements display in front of it
       thanks to absolute positioning.
      */}
      <ImageSection />
    </div>
  );
}

function Right() {
  const { setting }: { setting: TSettingDetail } = useContext(
    SettingStateContext
  );
  const columnCount = useProductDetailColumnCount();

  return (
    <div className={css.right}>
      <DescriptionAndPrice setting={setting} />
      <ShippingAndActions setting={setting} />
      <MatchingBandCallout setting={setting} />
    </div>
  );
}

function SingleColumn() {
  const { setting }: { setting: TSettingDetail } = useContext(
    SettingStateContext
  );
  const diamondId = useSelector(state => state.ringBuilder.diamond.id);

  return (
    <Container>
      <div className={css.singleColumn}>
        <DescriptionAndPrice setting={setting} />
        <ShippingAndActions setting={setting} />
        <ImageSection />
        <ImageThumbnails />
        {!diamondId && <StoneSizeSlider />}
        <Specifications />
        <MatchingBandCallout setting={setting} />
        <OtherMetals />
        <RecentlyViewed />
      </div>
    </Container>
  );
}

function MultipleColumns() {
  return (
    <Container>
      <LeftAndMiddle />
      <Right />
    </Container>
  );
}

function Loading() {
  return (
    <Container>
      <div className={css.loading}>
        <div>
          <img src={loadingIcon} alt='Loading icon' />
        </div>
        <p>Loading engagement ring...</p>
      </div>
    </Container>
  );
}

const GetSetting = React.memo(function GetSetting() {
  const [failed, setFailed] = useState(false);
  const { setSetting } = useContext(SettingDispatchContext);
  const dispatch = useDispatch();
  const { sku, setSku } = useContext(SkuContext);
  const { compatibility } = useContext(CompatibilityContext);

  useEffect(() => {
    if (compatibility.initializing) return;

    const getSetting = async () => {
      if (!sku) {
        const locationMatches =
          window && window.location.pathname.match(urlMatcher);
        const sku = locationMatches && locationMatches[1];

        if (!sku) {
          throw new Error('Unable to parse SKU from path.');
        }

        setSku(sku);
        return;
      }

      const axios = AxiosInstanceGenerator();

      try {
        const res = await axios.get(routes.apiDetail(sku), {
          headers: { 'Cache-Control': 'maxage=1200' }
        });

        setSetting(res.data);

        if (typeof document !== 'undefined') {
          document.title = res.data.meta_title;
          const desc = document.querySelector('meta[name=description]');

          desc && desc.setAttribute('content', res.data.meta_description);
        }

        dispatch({
          type: 'PUSH_RECENTLY_VIEWED_SETTING',
          payload: res.data
        });
      } catch (e) {
        if (typeof Honeybadger !== 'undefined') {
          Honeybadger.notify(e);
        }

        setFailed(true);
      }
    };

    getSetting().catch(handleError);
  }, [sku, compatibility.initializing]);

  if (failed) {
    throw new Error('Loading from the API failed');
  }

  return null;
});

function ProductDetail({ railsContext }) {
  const { setSku } = useContext(SkuContext);
  const { setting }: { setting: TSettingDetail } = useContext(
    SettingStateContext
  );
  const { compatibility } = useContext(CompatibilityContext);
  const urlSkuRef = useRef(null);
  const columns = useProductDetailColumnCount();

  // We only want to set it once, but not in useEffect because we need to do it
  // before DiamondCompatibility is mounted.
  if (!railsContext.serverSide && !urlSkuRef.current) {
    const locationMatches =
      window && window.location.pathname.match(urlMatcher);
    const sku = locationMatches && locationMatches[1];

    if (!sku) {
      throw new Error('Unable to parse SKU from path.');
    }

    urlSkuRef.current = sku;
  }

  // If a diamond is in DYOR that is not compatible with the setting that we
  // navigated to, display the setting from the same series/metal combo that IS
  // compatible.
  useEffect(() => {
    if (compatibility.initializing) return;

    if (compatibility.recommended) setSku(compatibility.recommended);
  }, [compatibility.initializing]);

  if (!setting || compatibility.initializing) {
    return (
      <Fragment>
        <div style={{ display: 'none' }}>
          <DiamondCompatibility settingSku={urlSkuRef.current} />
        </div>
        <Loading />
      </Fragment>
    );
  }

  return (
    <div>
      <ViewItemEvent setting={setting} />
      <GetUpdatedProductData />
      <DesignYourOwnRing />
      <DiamondCompatibility settingSku={urlSkuRef.current} />
      {columns > 1 ? <MultipleColumns /> : <SingleColumn />}
      <PageFooter />
    </div>
  );
}

function ErrorLoading() {
  return (
    <Container>
      <div>
        <h1>Something went wrong</h1>
        <p>
          Unfortunately, we were not able to load the item you are looking for.
          Please refresh the browser to try again.
        </p>
      </div>
    </Container>
  );
}

// We have to accept `railsContext` for client-side renders, while it comes from
// `useSSRComponent` in `props` on server-side renders.
export default (props: PropsType, railsContext: RailsContextType = {}) => (
  <ErrorBoundary fallbackComponent={<ErrorLoading />}>
    <StoreProvider store={ReactOnRails.getStore('SharedReduxStore')}>
      {/* Don't pull in setting until client render has hydrated */}
      <DyorDataWrapper fallback={<Loading />}>
        <CompatibilityProvider defaultCompatibility={{ initializing: true }}>
          <SkuProvider>
            <ImageProvider>
              <VideoProvider>
                <SettingProvider>
                  <GetSetting />
                  <ProductDetail {...{ railsContext, ...props }} />
                </SettingProvider>
              </VideoProvider>
            </ImageProvider>
          </SkuProvider>
        </CompatibilityProvider>
      </DyorDataWrapper>
    </StoreProvider>
  </ErrorBoundary>
);
